import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * General Animation System
 * 
 * This system provides a flexible way to create scroll-triggered animations
 * using data-attributes. It supports individual elements and grouped animations
 * with staggering effects.
 * 
 * Usage:
 * - Individual elements: Add data-animate="animation-type" to any element
 * - Grouped animations: Wrap elements in a container with data-animate-group
 * 
 * Available animation types:
 * - fade-up, fade-down, fade-left, fade-right, fade
 * - scale-up, scale-down
 * - slide-up, slide-down, slide-left, slide-right
 * 
 * Data attributes:
 * - data-animate: Animation type (required)
 * - data-animate-delay: Delay before animation starts (optional, default: 0)
 * - data-animate-duration: Animation duration (optional, default: 0.6)
 * - data-animate-position: ScrollTrigger start position (optional, default: 'bottom-=200')
 * - data-animate-ease: Easing function (optional, default: 'power2.out')
 * - data-animate-stagger: Stagger delay for grouped elements (optional, default: 0.1)
 */

// Check if device is desktop with mouse for performance optimization
function isDesktopWithMouse() {
  const hasHover = window.matchMedia('(hover: hover)').matches;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isDesktopSize = window.innerWidth >= 1024;
  
  // return hasHover && !isMobile && isDesktopSize;
}

// Animation configurations
const ANIMATION_CONFIGS = {
  'fade-up': { y: 30, opacity: 0 },
  'fade-down': { y: -30, opacity: 0 },
  'fade-left': { x: 30, opacity: 0 },
  'fade-right': { x: -30, opacity: 0 },
  'fade': { opacity: 0 },
  'scale-up': { scale: 0.8, opacity: 0 },
  'scale-down': { scale: 1.2, opacity: 0 },
  'slide-up': { y: 50, opacity: 0 },
  'slide-down': { y: -50, opacity: 0 },
  'slide-left': { x: 50, opacity: 0 },
  'slide-right': { x: -50, opacity: 0 }
};

// Default values
const DEFAULTS = {
  duration: 0.6,
  delay: 0,
  position: 'bottom-=200',
  ease: 'power2.out',
  stagger: 0.1
};

/**
 * Initialize the general animation system
 */
export function initGeneralAnimations() {
  try {
    // Skip animations on mobile devices for better performance
    // if (!isDesktopWithMouse()) {
    //   setElementsToFinalState();
    //   return;
    // }

    // Batch DOM queries for better performance
    const animatedElements = document.querySelectorAll('[data-animate]');
    const animationGroups = document.querySelectorAll('[data-animate-group]');
    
    // Track processed elements to avoid duplicates
    const processedElements = new Set();
    
    // Process individual elements first
    animatedElements.forEach(element => {
      if (element.closest('[data-animate-group]')) return; // Skip if part of a group
      if (processedElements.has(element)) return; // Skip if already processed
      
      const animation = element.dataset.animate;
      const delay = parseFloat(element.dataset.animateDelay) || DEFAULTS.delay;
      const duration = parseFloat(element.dataset.animateDuration) || DEFAULTS.duration;
      const triggerPosition = element.dataset.animatePosition || DEFAULTS.position;
      const ease = element.dataset.animateEase || DEFAULTS.ease;
      
      createAnimation(element, animation, delay, duration, triggerPosition, ease);
      processedElements.add(element);
    });

    // Process animation groups
    animationGroups.forEach(group => {
      const groupElements = group.querySelectorAll('[data-animate]');
      
      if (groupElements.length === 0) {
        console.warn('Animation group found with no animated children:', group);
        return;
      }
      
      const stagger = parseFloat(group.dataset.animateStagger) || DEFAULTS.stagger;
      const groupDelay = parseFloat(group.dataset.animateDelay) || DEFAULTS.delay;
      const triggerPosition = group.dataset.animatePosition || DEFAULTS.position;
      const ease = group.dataset.animateEase || DEFAULTS.ease;
      
      // Create grouped animation with single trigger
      createGroupedAnimation(group, groupElements, stagger, groupDelay, triggerPosition, ease);
      
      // Mark all group elements as processed
      groupElements.forEach(element => {
        processedElements.add(element);
      });
    });    
  } catch (error) {
    console.error('Error initializing animation system:', error);
    // Fallback: set all elements to final state
    setElementsToFinalState();
  }
}

/**
 * Set all animated elements to their final state without animation
 * Used for mobile devices or when animations are disabled
 */
function setElementsToFinalState() {
  const allAnimatedElements = document.querySelectorAll('[data-animate]');
  
  allAnimatedElements.forEach(element => {
    gsap.set(element, {
      y: 0,
      x: 0,
      scale: 1,
      opacity: 1
    });
  });
}

/**
 * Create grouped animation with single trigger
 * All child elements animate together when the group container enters the viewport
 */
function createGroupedAnimation(group, groupElements, stagger, groupDelay, triggerPosition, ease) {
  // Validate inputs
  if (!group || !groupElements.length) {
    console.warn('Invalid group or group elements provided to createGroupedAnimation');
    return;
  }
  
  // Set initial states for all elements
  groupElements.forEach(element => {
    const animation = element.dataset.animate;
    const elementDelay = parseFloat(element.dataset.animateDelay) || 0;
    
    if (!ANIMATION_CONFIGS[animation]) {
      console.warn(`Unknown animation type: ${animation}`, element);
      return;
    }
    
    const initialState = ANIMATION_CONFIGS[animation];
    
    // Set initial state with will-change for better performance
    gsap.set(element, {
      ...initialState,
      willChange: 'transform, opacity'
    });
  });
  
  // Create timeline for the group
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: group,
      start: `top ${triggerPosition}`,
      toggleActions: 'play none none none',
      onComplete: () => {
        // Remove will-change after animation completes for better performance
        groupElements.forEach(element => {
          gsap.set(element, { willChange: 'auto' });
        });
      }
    }
  });
  
  // Add animations to timeline with stagger
  groupElements.forEach((element, index) => {
    const animation = element.dataset.animate;
    const elementDelay = parseFloat(element.dataset.animateDelay) || 0;
    const duration = parseFloat(element.dataset.animateDuration) || DEFAULTS.duration;
    
    // Calculate total delay including group delay, stagger, and element delay
    const totalDelay = groupDelay + (stagger * index) + elementDelay;
    
    timeline.to(element, {
      y: 0,
      x: 0,
      scale: 1,
      opacity: 1,
      duration: duration,
      ease: ease
    }, totalDelay);
  });
}

/**
 * Create animation for a single element
 * This is the core animation function used by both individual and grouped animations
 */
function createAnimation(element, animation, delay, duration, triggerPosition, ease) {
  // Validate inputs
  if (!element || !element.nodeType) {
    console.warn('Invalid element provided to createAnimation');
    return;
  }
  
  if (!ANIMATION_CONFIGS[animation]) {
    console.warn(`Unknown animation type: ${animation}`, element);
    return;
  }
  
  // Validate numeric values
  if (isNaN(delay) || isNaN(duration)) {
    console.warn('Invalid delay or duration values', { delay, duration, element });
    return;
  }
  
  const initialState = ANIMATION_CONFIGS[animation];
  
  // Set initial state with will-change for better performance
  gsap.set(element, {
    ...initialState,
    willChange: 'transform, opacity'
  });
  
  // Create the animation
  gsap.to(element, {
    y: 0,
    x: 0,
    scale: 1,
    opacity: 1,
    duration: duration,
    delay: delay,
    ease: ease,
    scrollTrigger: {
      trigger: element,
      start: `top ${triggerPosition}`,
      toggleActions: 'play none none none',
      onComplete: () => {
        // Remove will-change after animation completes for better performance
        gsap.set(element, { willChange: 'auto' });
      }
    }
  });
}

/**
 * Refresh ScrollTrigger instances
 * Call this after dynamically adding new animated elements
 */
export function refreshAnimations() {
  ScrollTrigger.refresh();
}

/**
 * Kill all ScrollTrigger instances
 * Useful for cleanup or when switching pages
 */
export function killAnimations() {
  ScrollTrigger.killAll();
}

/**
 * Get animation statistics for debugging
 */
export function getAnimationStats() {
  const animatedElements = document.querySelectorAll('[data-animate]');
  const animationGroups = document.querySelectorAll('[data-animate-group]');
  const scrollTriggers = ScrollTrigger.getAll();
  
  return {
    totalAnimatedElements: animatedElements.length,
    totalAnimationGroups: animationGroups.length,
    totalScrollTriggers: scrollTriggers.length,
    animationTypes: [...new Set(Array.from(animatedElements).map(el => el.dataset.animate))],
    isAnimationsEnabled: isDesktopWithMouse()
  };
}

/**
 * Enable/disable animations globally
 */
export function setAnimationsEnabled(enabled) {
  if (!enabled) {
    killAnimations();
    setElementsToFinalState();
  } else {
    initGeneralAnimations();
  }
}

/**
 * Add animation to a single element programmatically
 */
export function addElementAnimation(element, options = {}) {
  const {
    animation = 'fade-up',
    delay = 0,
    duration = 0.6,
    position = 'bottom-=200',
    ease = 'power2.out'
  } = options;
  
  createAnimation(element, animation, delay, duration, position, ease);
}

// Auto-initialize after page fade in
if (document.body.classList.contains('page-loaded')) {
  initGeneralAnimations();
}
